// React Components
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import PropTypes from 'prop-types';

// Material UI Components
import { Grid, Paper } from '@mui/material';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import { Alert, AlertTitle } from '@mui/material';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// Audit Vault Components
import AccessDenied from "../components/common/AccessDenied";
import ServiceIsDown from "../components/common/ServiceIsDown";
import ProgressBar from "../components/common/ProgressBar";
import SharePointFileActivityDeletion from "../components/reports/SharePointFileActivityDeletion";
import SharePointPageViews from "../components/reports/SharePointPageViews";
import ImageSharePoint from '../imgs/iconSharePoint.svg';
import AuditGrowth from "../components/reports/AuditGrowth";
import PeriodDropDown from "../components/reports/PeriodDropDown";
import CustomerPageScaffold from "../components/common/CustomerPageScaffold";
import FilterPanel from "../components/common/FilterPanel";
import TenantSetupRequired from "../components/tenant/TenantSetupRequired";
import InsightsSearchBox from "../components/reports/InsightsSearchBox";

// Audit Vault Utilities
import { getCompanyByCompanyId } from "../utilities/common-company";
import { NumberBadge } from '../utilities/common-ui-utils';
import { REPORT_PERIOD_LIST, REPORT_ON_PERIOD_1WEEK, REPORT_ON_PERIOD_30DAYS, REPORT_ON_PERIOD_90DAYS, REPORT_ON_PERIOD_DEFAULT_DROPDOWNVALUE } from "../constants/constants-reportperiods";
import { formatAuditJobRunTimeDateServerTimeToGMT } from '../utilities/common-date-utils';
import { verifyUserHasTenantRolePermissions } from "../utilities/common-user-utils";
import { ROLE_CODE_TENANTREPORTREADER } from "../constants/constants-roles";
import { getHealthStatsByTenantId } from "../utilities/common-healthstats";

const theBreadcrumbPath = [
    { name: 'Home', link: '/' },
    { name: 'Reports', link: '/Reports/ReportsHome' },
    { name: 'SharePoint Online Reports', link: '/Reports/SharePoint' },
];

const theSectionTitle = "SharePoint Online Reports Dashboard";
const theSectionDesc = "Audit logs for SharePoint and OneDrive capture events such as site and list activity, document activity, site administration, sharing and access requests, user activity, and compliance related events.  Below is a visual representation of your essential SharePoint and OneDrive audit data with links to additional powerful reports to unlock important insights into your data.";
const isAdminPage = false;

// Common styles for report links inside tab panels
const linkListSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
};

const reportLinkSx = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    whiteSpace: 'normal',
    marginBottom: 0.5,
};

/*
Displays the SharePoint Reports home page for Company X.
*/
function ReportsSharepoint(props) {

    const { user, openCompanyNag, setOpenCompanyNag } = props;

    // Component Constants
    const [company, setCompany] = useState("");
    const [tenantHealthStats, setTenantHealthStats] = useState("");
    const [focusPeriodId, setFocusPeriodId] = useState(REPORT_ON_PERIOD_DEFAULT_DROPDOWNVALUE);
    const [accessDenied, setAccessDenied] = useState(false);

    const [loading, setLoading] = useState(true);
    const [serviceIsDownError, setServiceIsDownError] = useState(false);
    const navigate = useNavigate();
  
    const [totalSPAuditRecords, setTotalSPAuditRecords] = useState(null);
    const [loadingTotalSPAuditRecords, setLoadingTotalSPAuditRecords] = useState(false);
    const [totalSPAuditRecordsLoaded, setTotalSPAuditRecordsLoaded] = useState(false);

    const [selectedTenantOption, setSelectedTenantOption] = useState('');
    const [selectedPeriodOption, setSelectedPeriodOption] = useState('');
    const [tabValue, setTabValue] = useState(0);

    // Component Functions
    // Load TenantHealthStats given the tenantId.
    async function fetchTenantHealthStats(theTenantId) {
        setLoading(true);
        try {
            var response2 = await getHealthStatsByTenantId(theTenantId, 2, user);
            if (response2) {
                setTenantHealthStats(response2);
            }

        }
        catch (e) {
            console.log("ERROR: ReportSharePoint.fetchTenantHealthStats");
            console.log(e);
            setServiceIsDownError(true);
        }
        finally {
            setLoading(false);
        }
    }

    // Load the total sp audit records on demand.
    async function fetchTotalAuditRecordsOnDemand() {
        if (!user?.tenantId || loadingTotalSPAuditRecords || totalSPAuditRecordsLoaded) {
            return;
        }

        setLoadingTotalSPAuditRecords(true);

        try {
            // Heavier on-demand total counts load.
            const response = await getHealthStatsByTenantId(user.tenantId, 12, user);            
            if (response) {
                const spTotal = response.totalSharePointAuditRecords;
                setTotalSPAuditRecords(spTotal);
                setTotalSPAuditRecordsLoaded(true);                
            }
        }
        catch (e) {
            console.log("ERROR: Report.fetchTotalAuditRecordsOnDemand");
            console.log(e);
            setServiceIsDownError(true);
        }
        finally {
            setLoadingTotalSPAuditRecords(false);
        }
    }


    useEffect(() => {
        /*
        Check if companyId is specified, if not then load current User's company.                
        */
        async function fetchCompanyData() {
            setLoading(true);

            try {
                if (!verifyUserHasTenantRolePermissions(user, ROLE_CODE_TENANTREPORTREADER)) {
                    setAccessDenied(true);
                    return;
                }

                if (user && user.companyId) {
                    var response1 = await getCompanyByCompanyId(user.companyId, setServiceIsDownError);
                    if (response1) {

                        setCompany(response1);
                        // Set the default to the first item in the list if there is any.
                        if (response1.tenantList && response1.tenantList.length > 0) {
                            setSelectedTenantOption(response1.tenantList[0]);  // Set to the first item in the list.
                            // Set the default period in the drop down list (we default to 30 days).                            
                            setSelectedPeriodOption(REPORT_PERIOD_LIST[REPORT_ON_PERIOD_DEFAULT_DROPDOWNVALUE]);
                            fetchTenantHealthStats(response1.tenantList[0].id);
                        }
                    }
                }
            }
            catch (e) {
                console.log("ERROR: ReportSharePoint.fetchCompanyData");
                console.log(e);
                setServiceIsDownError(true);
            }
            finally {
                setLoading(false);
            }
        }

        fetchCompanyData();
    }, []);

    function TabPanel(props) {
        const { children, value, index, ...other } = props;

        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                {...other}
            >
                {value === index && (
                    <Box
                        sx={{
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            // Force all MUI Links inside this panel to be left-aligned and full-width
                            "& a.MuiLink-root": {
                                display: "block",
                                textAlign: "left",
                                whiteSpace: "normal",
                                width: "100%",
                            },
                        }}
                    >
                        {children}
                    </Box>
                )}
            </div>
        );
    }


    TabPanel.propTypes = {
        children: PropTypes.node,
        value: PropTypes.number.isRequired,
        index: PropTypes.number.isRequired,
    };

    function a11yProps(index) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Event handlers for drop down lists.
    const handleTenantOptionChange = (event) => {
        setSelectedTenantOption(event.target.value);
        fetchTenantHealthStats(event.target.value.id);
    };
    const handlePeriodOptionChange = (event) => {
        setSelectedPeriodOption(event.target.value);
        setFocusPeriodId(event.target.value.id);
    };

    // Component UI
    if (loading) {
        console.log("Loading Data ....")
        return (<ProgressBar message="Loading ..." loading={loading} />);
    }

    if (serviceIsDownError) {
        return (<><ServiceIsDown></ServiceIsDown></>);
    }

    // Get the total audit record count.
    var totalAuditRecords = 0;
    if (tenantHealthStats.totalSharePointAuditRecords) {
        totalAuditRecords = tenantHealthStats.totalSharePointAuditRecords;
    }

    // A valid Tenant is chosen from the textfield selection drop down list.
    return (
        <>
            <CustomerPageScaffold
                breadcrumbPaths={theBreadcrumbPath}
                sectionTitle={theSectionTitle}
                sectionDescription={theSectionDesc}
                isAdminPage={isAdminPage}
                company={company}
                selectedTenant={selectedTenantOption}
                openCompanyNag={openCompanyNag}
                setOpenCompanyNag={setOpenCompanyNag}
                headerAdornment={<Box component="img" src={ImageSharePoint} alt="SharePoint Reports" sx={{ width: 75, height: 'auto' }} />}
            />

            <Box>
                <Grid container spacing={2}>
                    {accessDenied && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <AccessDenied />
                        </Grid>
                    )}

                    {!selectedTenantOption && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <Alert severity="error">
                                <AlertTitle>Your Company has not yet completed the setup process or you have not yet specified a valid Tenant.</AlertTitle>
                            </Alert>
                        </Grid>
                    )}

                    {tenantHealthStats.status === 0 && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <TenantSetupRequired />
                        </Grid>
                    )}

                    {!accessDenied && selectedTenantOption && tenantHealthStats.status >= 1 ? (
                        <>
                            <Grid item xs={12}>
                                <FilterPanel title="Dashboard filters">
                                    <PeriodDropDown
                                        selectedPeriodOption={selectedPeriodOption}
                                        handlePeriodOptionChange={handlePeriodOptionChange}
                                    />
                                </FilterPanel>
                            </Grid>
                            {/* Last updated text */}
                            <Grid item xs={12} sx={{ p: 2 }}>
                                Audit data last updated on:{" "}
                                {formatAuditJobRunTimeDateServerTimeToGMT(
                                    tenantHealthStats.sharePointAuditLastSuccessfulRunTime
                                )}
                                <br />
                            </Grid>

                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                    <Box sx={{ height: '100%' }}>
                                        <AuditGrowth tenantId={user.tenantId} periodToUse={focusPeriodId} reportType="SharePoint" />
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%', overflowWrap: 'break-word' }}>
                                    <SharePointFileActivityDeletion
                                        user={user}
                                        companyId={company.id}
                                        tenantId={user.tenantId}
                                        periodToUse={focusPeriodId}
                                        loadData={false}
                                        reportOnDate={new Date()}
                                        displayDataSource={0}
                                        filterByColumn={"operation"}
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                    <Box sx={{ height: '100%' }}>
                                        <SharePointPageViews
                                            user={user}
                                            companyId={company.id}
                                            tenantId={user.tenantId}
                                            periodToUse={focusPeriodId}
                                            loadData={false}
                                            filterByColumn={"Operation"}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>

                            { /* Display total sp audit record count on demand (lazy load).  Optimizes performance to show only on demand.  */}
                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                    <Box sx={{ height: '100%' }}>
                                        <Typography
                                            component="div"
                                            variant="h6"
                                            sx={{ fontSize: 16, pb: 2 }}
                                        >
                                            Total Audit Records
                                        </Typography>

                                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                                            {!totalSPAuditRecordsLoaded && !loadingTotalSPAuditRecords && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={fetchTotalAuditRecordsOnDemand}
                                                >
                                                    Show total records
                                                </Button>
                                            )}

                                            {loadingTotalSPAuditRecords && (
                                                <Typography
                                                    component="div"
                                                    variant="body2"
                                                    sx={{ fontSize: 15 }}
                                                >
                                                    Loading count...
                                                </Typography>
                                            )}

                                            {totalSPAuditRecordsLoaded && !loadingTotalSPAuditRecords && (
                                                <TableContainer sx={{ mt: 1 }}>
                                                    <Table style={{ width: '100%', fontSize: 'inherit', fontWeight: 'inherit' }}>
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell style={{ border: 'none', padding: 2 }}>
                                                                    SharePoint
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    style={{ border: 'none', padding: 2 }}
                                                                >
                                                                    <NumberBadge
                                                                        number={totalSPAuditRecords?.toLocaleString()}
                                                                        bgcolor="#ff6384"
                                                                        txtcolor="white"
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            
                            <InsightsSearchBox colSpanVal={8} workloadName={"SharePoint"} />

                            {/* Tabs + report link lists */}
                            <Grid item xs={12}>
                                <Box sx={{ width: '100%' }}>
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                        <Tabs
                                            value={tabValue}
                                            onChange={handleTabChange}
                                            aria-label="SharePoint Reports Tab"
                                            variant="scrollable"
                                            scrollButtons="auto"
                                        >
                                            <Tab label="Activity Reports" {...a11yProps(0)} />
                                            <Tab label="User Reports" {...a11yProps(1)} />
                                            <Tab label="Top Activity" {...a11yProps(2)} />
                                        </Tabs>
                                    </Box>

                                    <TabPanel value={tabValue} index={0}>
                                        <Box sx={linkListSx}>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointItemAuditHistoryReport`)} component="button">
                                                Get Audit History for an Item
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharepointAuditHistory/1`)} component="button">
                                                View SharePoint Audit History (to be accessed from SPO)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointAllActivityBySiteReport`)} component="button">
                                                All Activity by Site Collection, Location or File
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointSitePermsAndChangeHistoryReport`)} component="button">
                                                Site Permissions Change History
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityDeletionReport/${REPORT_ON_PERIOD_30DAYS}/operation`)} component="button">
                                                SharePoint File Activity - Deletions (Last 30 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityDeletionReport/${REPORT_ON_PERIOD_30DAYS}/siteUrl`)} component="button">
                                                SharePoint File Activity - Deletion by Site (Last 30 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityDeletionReport/${REPORT_ON_PERIOD_90DAYS}/operation`)} component="button">
                                                SharePoint File Activity - Deletions (Last 3 months)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityDeletionReport/${REPORT_ON_PERIOD_90DAYS}/siteUrl`)} component="button">
                                                SharePoint File Activity - Deletion by Site (Last 3 months)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityViewAndDownloadReport/${REPORT_ON_PERIOD_1WEEK}/operation`)} component="button">
                                                SharePoint File Activity - Views and Downloads (Last 7 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityViewAndDownloadReport/${REPORT_ON_PERIOD_1WEEK}/siteUrl`)} component="button">
                                                SharePoint File Activity - Views and Downloads by Site (Last 7 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityViewAndDownloadReport/${REPORT_ON_PERIOD_30DAYS}/operation`)} component="button">
                                                SharePoint File Activity - Views and Downloads (Last 30 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityViewAndDownloadReport/${REPORT_ON_PERIOD_30DAYS}/siteUrl`)} component="button">
                                                SharePoint File Activity - Views and Downloads by Site (Last 30 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityNewAndEditReport/${REPORT_ON_PERIOD_1WEEK}/operation`)} component="button">
                                                SharePoint File Activity - New Files and Edits (Last 7 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityNewAndEditAndFileSyncReport/${REPORT_ON_PERIOD_1WEEK}/operation`)} component="button">
                                                SharePoint File Activity - New Files and Edits - Include OneDrive Sync (Last 7 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityNewAndEditReport/${REPORT_ON_PERIOD_1WEEK}/siteUrl`)} component="button">
                                                SharePoint File Activity - New Files and Edits by Site (Last 7 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityNewAndEditAndFileSyncReport/${REPORT_ON_PERIOD_1WEEK}/siteUrl`)} component="button">
                                                SharePoint File Activity - New Files and Edits by Site - Include OneDrive Sync (Last 7 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityNewAndEditReport/${REPORT_ON_PERIOD_30DAYS}/operation`)} component="button">
                                                SharePoint File Activity - New Files and Edits (Last 30 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityNewAndEditAndFileSyncReport/${REPORT_ON_PERIOD_30DAYS}/operation`)} component="button">
                                                SharePoint File Activity - New Files and Edits - Include OneDrive Sync (Last 30 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityNewAndEditReport/${REPORT_ON_PERIOD_30DAYS}/siteUrl`)} component="button">
                                                SharePoint File Activity - New Files and Edits by Site (Last 30 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointFileActivityNewAndEditAndFileSyncReport/${REPORT_ON_PERIOD_30DAYS}/siteUrl`)} component="button">
                                                SharePoint File Activity - New Files and Edits by Site - Include OneDrive Sync (Last 30 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointPageViewsReport/${REPORT_ON_PERIOD_30DAYS}/operation`)} component="button">
                                                SharePoint Page Views (Last 30 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointPageViewsReport/${REPORT_ON_PERIOD_30DAYS}/userId`)} component="button">
                                                SharePoint Page Views by User (Last 30 days)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointPageViewsReport/${REPORT_ON_PERIOD_90DAYS}/operation`)} component="button">
                                                SharePoint Page Views (Last 3 months)
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointPageViewsReport/${REPORT_ON_PERIOD_90DAYS}/userId`)} component="button">
                                                SharePoint Page Views by User (Last 3 months)
                                            </Link>
                                        </Box>
                                    </TabPanel>

                                    <TabPanel value={tabValue} index={1}>
                                        <Box sx={linkListSx}>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointAllActivityByUserReport`)} component="button">
                                                All Activity by User
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointDeletionsByUserReport`)} component="button">
                                                Documents and Folders Deleted by User
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointCreationsByUserReport`)} component="button">
                                                Documents and Folders Created by User
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointEditsByUserReport`)} component="button">
                                                Documents and Folders Edited by User
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointViewsByUserReport`)} component="button">
                                                Documents Viewed by User
                                            </Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate(`/Reports/SharePointAllActivityByIPAddressReport`)} component="button">
                                                All Activity by IP Address
                                            </Link>
                                        </Box>
                                    </TabPanel>
                                    <TabPanel value={tabValue} index={2}>
                                        <Box sx={linkListSx}>
                                            <Link sx={reportLinkSx} onClick={() => navigate('/Reports/TopActivity/sharepoint/users')} component="button">Top Users by Activity</Link>
                                            <Link sx={reportLinkSx} onClick={() => navigate('/Reports/TopActivity/sharepoint/operations')} component="button">Top Operations by Activity</Link>
                                        </Box>
                                    </TabPanel>
                                </Box>
                            </Grid>
                        </>
                    ) : (
                        !accessDenied &&
                        selectedTenantOption &&
                        tenantHealthStats.status !== 0 && (
                            <Grid item xs={12} sx={{ p: 2 }}>
                                <ProgressBar message="Loading ..." loading={true} />
                            </Grid>
                        )
                    )}

                    <Grid item xs={12}>
                        <br />
                        Need a custom or new report? Provide us with your{" "}
                        <Link onClick={() => navigate(`/Support/SupportHome`)} component="button">
                            Product Feedback
                        </Link>.
                        <br />
                        * Unless otherwise specified as UTC, graphs on page are using your browser's local timezone.
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}

export default ReportsSharepoint;
